import { FormValues, getValue, InputFormGroup, InputFormSidePanelComponent } from "o4a-react";
import * as React from "react";
import apiClients from "../../apiClients";
import * as Messages from "../../codegen/Messages";
import { Fields, MysqlBackupEditPanel } from "../../components/MysqlManagementEditPanel/MysqlBackupEditPanel";
import {
  MdsBackupPolicy,
  MdsDbSystemPitrPolicy,
  UpdateMdsBackupPolicyDetails,
} from "../../gen/clients/mchub-azure-api-client-mds";
import { parseId } from "../../helpers/idHelper";
import { useMutationCall } from "../../hooks/useMutationCall";
import {
  AsyncNotificationMethodKey,
  AsyncNotificationPolledResponseKey,
  MDS_UPDATE_POLL_DELAY,
  MDS_UPDATE_POLL_INTERVAL,
} from "../../models/AsyncNotificationProviders";
import { getOciRegion } from "../../utils";
import { OperationActionProps } from "../OperationTypes";

export interface MysqlEditBackupConfigProps extends OperationActionProps {
  databaseId: string | undefined;
  backupPolicy: MdsBackupPolicy | undefined;
}

const MysqlEditBackupConfig = (
  { targetId, location, databaseId, backupPolicy, onExecute, onCancel }: MysqlEditBackupConfigProps,
): JSX.Element => {
  const { subscriptionId, resourceGroup, resourceName } = parseId(databaseId);
  const resourceGroupName = decodeURIComponent(resourceGroup || "");
  const dbSystemName = decodeURIComponent(resourceName || "");

  const { invokeCall: invokeMdsUpdate } = useMutationCall(
    { method: apiClients.withRegion(getOciRegion(location)).mdsDatabaseApi.updateMdsDbSystem },
  );
  const [sidePanelRef, setSidePanelRef] = React.useState<InputFormSidePanelComponent>(
    {} as InputFormSidePanelComponent,
  );

  const buildPitrPolicy = (formValues: FormValues): MdsDbSystemPitrPolicy => {
    const pitr: MdsDbSystemPitrPolicy = { isEnabled: getValue(formValues, Fields.PitrPolicy, InputFormGroup) };
    return pitr;
  };

  const buildDbBackupConfig = (formValues: FormValues): UpdateMdsBackupPolicyDetails => {
    const isEnabled = getValue(formValues, Fields.AutoBackupEnabled, InputFormGroup);
    const backupDetails: UpdateMdsBackupPolicyDetails = {
      isEnabled,
      windowStartTime:
        // eslint-disable-next-line max-len
        isEnabled ? getValue(formValues, Fields.WindowStartTime, InputFormGroup)[0].id : undefined,
      retentionInDays:
        isEnabled ? Number.parseInt(
          getValue(formValues, Fields.RetentionInDays, InputFormGroup),
          10,
        ) : undefined,
      pitrPolicy:
       isEnabled ? buildPitrPolicy(formValues) : undefined,
    };
    return backupDetails;
  };

  const onUpdate = (formValues: FormValues): void => {
    const autoBackupEnabled = getValue(formValues, Fields.AutoBackupEnabled, InputFormGroup);
    // TODO:
    //  - When the form validator is implemented for the InputFormSidePanel
    //  - Use form validator instead and remove this 'validation'
    if (autoBackupEnabled) {
      const backupWindow = getValue(formValues, Fields.WindowStartTime, InputFormGroup);
      const recoveryWindow = getValue(formValues, Fields.RetentionInDays, InputFormGroup);
      const errors: string[] = [];
      if (!backupWindow) {
        errors.push(Messages.validation.required());
      }
      if (!recoveryWindow) {
        errors.push(Messages.validation.required());
      }
      if (errors.length > 0) {
        return;
      }
    }

    const backupPolicyConfig = buildDbBackupConfig(formValues);

    const invokeOptions = {
      onSuccess: onExecute,
      onFailure: sidePanelRef?.allowResubmit,
      notification: {
        inProgress: {
          title: Messages.notifications.inProgress.titles.updateMysqlBackup(),
          message: Messages.notifications.inProgress.messages.updateMysqlBackup(dbSystemName),
        },
        success: {
          title: Messages.notifications.success.titles.updateMysqlBackup(),
          message: Messages.notifications.success.messages.updateMysqlBackup(dbSystemName),
        },
        failure: {
          title: Messages.notifications.failure.titles.updateMysqlBackup(),
          message: Messages.notifications.failure.messages.updateMysqlBackup(dbSystemName),
        },
        asyncPolling: {
          methodKey: AsyncNotificationMethodKey.MDS_GET,
          methodArgs: {
            subscriptionId,
            resourceGroupName,
            dbSystemName,
          },
          location,
          responseMethodKey: AsyncNotificationPolledResponseKey.MDS_UPDATED_CHECK,
          delay: MDS_UPDATE_POLL_DELAY,
          interval: MDS_UPDATE_POLL_INTERVAL,
        },
      },
    };
    invokeMdsUpdate({
      subscriptionId,
      resourceGroupName,
      dbSystemName,
      updateMdsDbSystemDetails: { backupPolicy: backupPolicyConfig },
    }, invokeOptions);
  };

  return (
    <MysqlBackupEditPanel
      targetId={targetId}
      dbBackup={backupPolicy}
      onSubmit={onUpdate}
      onClose={onCancel}
      componentRef={setSidePanelRef}
    />
  );
};

export const newMysqlEditBackupConfig = (
  props: MysqlEditBackupConfigProps,
): JSX.Element => (<MysqlEditBackupConfig {...props} />);
